import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AUTH_FIELD_CLASSES,
  AUTH_LABEL_CLASSES,
  AUTH_LINK_CLASSES,
  AUTH_PRIMARY_ACTION_CLASSES,
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { validateEmail } from "@/utils/sanitization";
import { logger } from "@/utils/logger";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [rateLimited, setRateLimited] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    let timer: number | undefined;

    if (timeLeft > 0) {
      timer = window.setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && rateLimited) {
      setRateLimited(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [timeLeft, rateLimited]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset states
    setErrorMessage(null);

    if (!email) {
      toast.error(t.forms.error.allFieldsRequired);
      return;
    }

    if (!validateEmail(email)) {
      toast.error(t.forms.error.invalidEmail);
      return;
    }

    setLoading(true);

    try {
      logger.info("auth", "reset-password-attempt", "User attempting to reset password", {
        emailDomain: email.split("@")[1],
      });

      // Use direct Supabase call for password reset (recommended approach)
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: redirectUrl,
      });

      if (resetError) {
        logger.warn("auth", "reset-password-failed", "Failed to send reset email", {
          error: resetError.message,
          emailDomain: email.split("@")[1],
        });

        if (
          resetError.message?.includes("security purposes") ||
          resetError.message?.includes("rate limit") ||
          resetError.status === 429
        ) {
          setRateLimited(true);
          setTimeLeft(60);
          setErrorMessage(t.forms.error.tooManyAttempts);
          toast.error(t.forms.error.tooManyAttempts);
        } else {
          setErrorMessage(resetError.message || t.forms.error.unexpectedError);
          toast.error(resetError.message || t.forms.error.unexpectedError);
        }
      } else {
        logger.info("auth", "reset-password-email-sent", "Password reset email sent successfully");
        toast.success(t.forms.success.passwordResetSent);
        setEmailSent(true);
      }
    } catch (error) {
      logger.error(
        "auth",
        "reset-password-error",
        "Unexpected error during password reset",
        error as Error
      );
      setErrorMessage(t.forms.error.unexpectedError);
      toast.error(t.forms.error.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthPageHeader
        title={emailSent ? t.forms.passwordReset.checkEmailTitle : t.forms.passwordReset.title}
        description={
          emailSent
            ? t.forms.passwordReset.checkEmailDescription
            : t.forms.passwordReset.description
        }
        icon={emailSent ? <MailCheck className="h-6 w-6" /> : undefined}
        iconClassName={emailSent ? "bg-green-100 text-green-600" : undefined}
      />

      {(rateLimited || errorMessage) && !emailSent && (
        <Alert variant="destructive" className="mb-5 rounded-xl">
          <AlertDescription>
            {errorMessage || t.forms.error.tooManyAttempts}
            {rateLimited && timeLeft > 0 && (
              <div className="mt-2 text-sm font-medium">
                {t.forms.passwordReset.codeExpires}: {timeLeft} {t.forms.passwordReset.seconds}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {!emailSent ? (
        <form onSubmit={handleSendCode} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className={AUTH_LABEL_CLASSES}>
              {t.forms.login.emailLabel}
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t.forms.login.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || rateLimited}
              className={AUTH_FIELD_CLASSES}
            />
          </div>

          <Button
            type="submit"
            className={AUTH_PRIMARY_ACTION_CLASSES}
            disabled={loading || rateLimited}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.forms.status.saving}
              </>
            ) : (
              <>
                {t.forms.passwordReset.sendInstructionsButton}
                <ArrowRight className="h-[17px] w-[17px]" />
              </>
            )}
          </Button>
        </form>
      ) : (
        <div className="space-y-5">
          <p className="text-[14px] leading-6 text-muted-foreground">
            {t.forms.success.checkEmail}
          </p>
          <Button
            variant="outline"
            className={AUTH_SECONDARY_ACTION_CLASSES}
            onClick={() => {
              setEmailSent(false);
              setEmail("");
            }}
          >
            {t.forms.passwordReset.sendInstructionsButton}
          </Button>
        </div>
      )}

      <p className="mt-6 text-center text-[13.5px] text-muted-foreground">
        <Link to="/login" className={AUTH_LINK_CLASSES}>
          {t.forms.passwordReset.backToLogin}
        </Link>
      </p>
    </AuthPageLayout>
  );
}
